import type React from "react";

import { faCircleCheck, faCircleInfo, faTriangleExclamation, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styled, { css } from "styled-components";
import type { RuleSet } from "styled-components";

const iconStyles = {
  height: "22px",
  width: "22px",
  marginRight: "12px",
};

const getIcon = (icon: Variant) => {
  switch (icon) {
    case "danger":
      return <FontAwesomeIcon color="#FF5959" icon={faXmark} style={iconStyles} />;
    case "info":
      return <FontAwesomeIcon color="#1274E3" icon={faCircleInfo} style={iconStyles} />;
    // Saved variant is used for handling the usage in MedicalNoteFormButtons.tsx
    case "saved":
    case "success":
      return <FontAwesomeIcon color="#57cb75" icon={faCircleCheck} style={iconStyles} />;
    case "warning":
      return <FontAwesomeIcon color="#ECA073" icon={faTriangleExclamation} style={iconStyles} />;
    default:
      return null;
  }
};

type Variant = "danger" | "info" | "saved" | "success" | "warning";
interface Props {
  variant?: Variant;
  style?: Pick<React.CSSProperties, "margin" | "marginBottom" | "marginLeft" | "marginRight" | "marginTop" | "zIndex">;
  children: React.ReactNode;
  className?: string;
}

export const Notification: React.FC<Props> = ({ variant = "info", style, children, className }) => {
  return (
    <Container data-testid={`notification-${variant}`} $variant={variant} style={style} className={className}>
      {getIcon(variant)}
      {children}
    </Container>
  );
};

const styles: Record<Variant, RuleSet> = {
  danger: css`
    background: ${({ theme }) => theme.colors.pastels.red};
  `,
  info: css`
    background: ${({ theme }) => theme.colors.pastels.blue};
  `,
  saved: css`
    background: ${({ theme }) => theme.colors.pastels.green};
  `,
  success: css`
    background: ${({ theme }) => theme.colors.pastels.green};
  `,
  warning: css`
    background: ${({ theme }) => theme.colors.pastels.orange};
  `,
};

const Container = styled.div<{ $variant: Variant }>`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  padding: 12px;
  color: ${({ theme }) => theme.colors.redesign.db100};
  border-radius: 8px;
  a {
    text-decoration: underline;
  }
  ${({ theme }) => ({ ...theme.font.body2 })};
  ${({ $variant }) => styles[$variant]}
`;
